import {
  Button,
  Card,
  Group,
  MultiSelect,
  NumberInput,
  Radio,
  Select,
  SimpleGrid,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DatePickerInput, type DateValue } from '@mantine/dates';
import { type FormEvent, useEffect, useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { useParams } from 'react-router-dom';
import {
  useClientIPPFormContext,
  useRequirementForm,
} from '../ClientIppSection';
import {
  getFetcher,
  postFetcher,
  putFetcher,
} from '@/api/fetchers';
import { DaySelectorCard } from '../Cards/DaySelector';
import { dateFormat } from '@/index';
import dayjs from 'dayjs';
import { isEmpty, isEmptyForm } from '@/utils/functions';
import { type Requirement } from '@/schemas/API/GET/client/carePlan/requirement/response';
import {
  type Tag,
  type Tags,
} from '@/schemas/API/GET/tag/response';
import { MappingToComboBoxItem } from '@/schemas/converters/mappingShemasConverter';
import { type Mapping } from '@/schemas/API/GET/mapping/response';
import { requirementToUpdateRequirement } from '@/schemas/converters/clientSchemasConverter';
import { goodNotification } from '@/components/Notification/Notification';
import { displayErrorOnMantineForm } from '@/utils/error/errorHandler';
import TimeInput from '@/components/Input/TimeInput/TimeInput';

export const RequirementModal = ({
  closeModal,
  requirementId,
}: {
  closeModal: () => void;
  requirementId: number | null;
}): JSX.Element => {
  const [selectedDaysInMonth, setSelectedDaysInMonth] = useState<number[]>([]);
  const [selectedDaysInWeek, setSelectedDaysInWeek] = useState<number[]>([]);
  const [repetition, setRepetition] = useState('0');
  const [initialized, setInitialized] = useState(false);
  const { data: sexMap } = useSWR<Mapping>('mappings/sexes', getFetcher);

  const { clientId } = useParams<{
    clientId: string;
  }>();

  const careplanForm = useClientIPPFormContext();
  const requirementForm = useRequirementForm();

  const { data: requirement } = useSWR<Requirement>(
      requirementId !== null
          ? `clients/${clientId}/careplans/${careplanForm.values.id}/requirements/${requirementId}`
          : null,
      getFetcher
  );

  useEffect(() => {
    if (!requirement) {
      return;
    }
    requirementForm.setValues(requirement);
    const getRec = handleRepetition();
    if (getRec !== '0') {
      setRepetition(getRec);
    }
    setInitialized(true);
  }, [requirement]);

  useEffect(() => {
    const getRec = handleRepetition();
    if (getRec !== '0' && initialized) {
      setRepetition(getRec);
    }
  }, [repetition, initialized]);

  useEffect(() => {
    if (requirementId === null) {
      requirementForm.setValues({
        name: 'Requirement',
        location: null,
        carePlanId: null,
        duration: 1,
        startTime: '09:00:00',
        boundStartTime: '09:00:00',
        boundEndTime: '16:00:00',
        prefSex: null,
        startDate: '2024-01-01',
        note: null,
        recur: null,
        tags: [],
      });
      setRepetition('1');
    }
  }, []);
  useEffect(() => {
    if (
      !isEmptyForm(requirementForm) &&
      repetition !== '0' &&
      requirementForm.values.recur?.freq === 'WEEKLY'
    ) {
      requirementForm.setFieldValue('recur.byWeekDay', selectedDaysInWeek);
    }
  }, [selectedDaysInWeek]);

  const { data: tags } = useSWR<Tags>(`tags`, getFetcher);
  const daysInWeek = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];

  const { mutate } = useSWRConfig();

  const handleWeekDaySelect = (number: number): void => {
    if (selectedDaysInWeek?.includes(number)) {
      setSelectedDaysInWeek(
        selectedDaysInWeek.filter(selected => selected !== number)
      );
    } else {
      setSelectedDaysInWeek([...(selectedDaysInWeek ?? []), number]);
    }
  };

  function handleRepetition(): string {
    if (!requirementForm.values.recur) return '1';
    if (requirementForm.values.recur.freq === 'DAILY') return '2';
    if (
      requirementForm.values.recur.freq === 'WEEKLY' &&
      requirementForm.values.recur.interval === 1
    ) {
      requirementForm.values.recur.byWeekDay !== null &&
        setSelectedDaysInWeek(requirementForm.values.recur.byWeekDay);
      return '3';
    }

    if (
      requirementForm.values.recur.freq === 'WEEKLY' &&
      requirementForm.values.recur.interval === 2
    ) {
      requirementForm.values.recur.byWeekDay !== null &&
        setSelectedDaysInWeek(requirementForm.values.recur.byWeekDay);
      return '4';
    }
    if (requirementForm.values.recur.freq === 'MONTHLY') {
      requirementForm.values.recur.byMonthDay !== null &&
        setSelectedDaysInMonth(requirementForm.values.recur.byMonthDay);
      return '5';
    }
    return '0';
  }

  const updateRequirement = async (): Promise<void> => {
    try {
      const carePlanUpdate = requirementToUpdateRequirement(
        requirementForm.values
      );

      await putFetcher(
        `clients/${clientId}/careplans/${careplanForm.values.id}/requirements/${requirementId}`,
        carePlanUpdate
      );

      mutate(
        `clients/${clientId}/careplans/${careplanForm.values.id}/requirements/${requirementId}`
      );
      mutate(
        `clients/${clientId}/careplans/${careplanForm.values.id}/requirements`
      );

      goodNotification('Změny byly uloženy');
      closeModal();
    } catch (err) {
      displayErrorOnMantineForm(err, requirementForm.setFieldError);
    }
  };

  const createRequirement = async (): Promise<void> => {
    try {
      const carePlanUpdate = requirementToUpdateRequirement(
        requirementForm.values
      );

      await postFetcher(
        `clients/${clientId}/careplans/${careplanForm.values.id}/requirements`,
        carePlanUpdate
      );

      goodNotification('Požadavek byl vytvořen');
      mutate(
        `clients/${clientId}/careplans/${careplanForm.values.id}/requirements`
      );
      closeModal();
    } catch (err) {
      displayErrorOnMantineForm(err, requirementForm.setFieldError);
    }
  };

  if (isEmptyForm(requirementForm)) {
    return <>Wait a second</>;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    if (isEmpty(requirementForm.values.note))
      requirementForm.setFieldValue('note', null);
    event.preventDefault();
    if (requirementId === null) createRequirement();
    else updateRequirement();
  }

  return (
    <form className="flex flex-col gap-4 p-4" onSubmit={handleSubmit}>
      <TextInput
        label="Název požadavku"
        placeholder="Název požadavku"
        variant="filled"
        {...requirementForm.getInputProps('name')}
        radius="md"
        size="md"
        data-testid="requirement-name"
      />
      <DatePickerInput
        label={repetition === '1' ? 'Den' : 'Počáteční den'}
        variant="filled"
        value={dayjs(requirementForm.values.startDate, dateFormat).toDate()}
        onChange={(d: DateValue) => {
          const date: string = dayjs(d).format(dateFormat);
          requirementForm.setFieldValue('startDate', date);
        }}
        radius="md"
        size="md"
        error={requirementForm.errors?.startDate}
        data-testid="requirement-start-date"
      />
      <Radio.Group
        value={repetition}
        onChange={setRepetition}
        name="SelectRepetition"
        label="Opakování"
        data-testid="requirement-repetition"
      >
        <SimpleGrid spacing="sm">
          <Radio
            value="1"
            label="Jednorázově"
            pt="xs"
            onChange={() => requirementForm.setFieldValue('recur', null)}
          />
          <Radio
            value="2"
            label="Každý den"
            onChange={() =>
              requirementForm.setFieldValue('recur', {
                freq: 'DAILY',
                byWeekDay: null,
                byMonthDay: null,
                interval: 1,
              })
            }
          />
          <Radio
            value="3"
            label="Každý týden"
            onChange={() => {
              requirementForm.setFieldValue('recur', {
                interval: 1,
                freq: 'WEEKLY',
                byMonthDay: null,
                byWeekDay: selectedDaysInWeek,
              });
            }}
          />
          <Radio
            value="4"
            label="Ob-týden"
            onChange={() =>
              requirementForm.setFieldValue('recur', {
                interval: 2,
                freq: 'WEEKLY',
                byMonthDay: null,
                byWeekDay: selectedDaysInWeek,
              })
            }
          />
          <Radio
            value="5"
            label="Konkrétní dny v měsíci"
            onChange={() =>
              requirementForm.setFieldValue('recur', {
                interval: 1,
                freq: 'MONTHLY',
                byMonthDay: selectedDaysInMonth,
                byWeekDay: null,
              })
            }
          />
        </SimpleGrid>
      </Radio.Group>
      {(repetition === '3' || repetition === '4') && (
        <Card withBorder mt="xs" radius="md">
          <Group justify="space-evenly">
            {daysInWeek.map((element, elementId) => (
              <Button
                size="sm"
                radius="md"
                variant={
                  selectedDaysInWeek.includes(elementId) ? 'filled' : 'default'
                }
                key={element}
                onClick={() => {
                  handleWeekDaySelect(elementId);
                }}
              >
                {element}
              </Button>
            ))}
          </Group>
        </Card>
      )}
      {repetition === '5' && (
        <DaySelectorCard days={selectedDaysInMonth} repetition={repetition} />
      )}

      <TimeInput
        label="Začátek"
        size="md"
        {...requirementForm.getInputProps('startTime')}
        data-testid="requirement-start-time"
      ></TimeInput>

      <TimeInput
        label="Volný čas od"
        size="md"
        {...requirementForm.getInputProps('boundStartTime')}
        data-testid="requirement-bound-start-time"
      ></TimeInput>

      <TimeInput
        label="Volný čas do"
        size="md"
        {...requirementForm.getInputProps('boundEndTime')}
        data-testid="requirement-bound-end-time"
      ></TimeInput>
      <NumberInput
        label="Doba trvání"
        variant="filled"
        placeholder="none"
        value={requirementForm.values.duration}
        onChange={val => {
          if (typeof val === 'number') {
            requirementForm.setFieldValue('duration', val);
          }
        }}
        suffix=", minuty"
        radius="md"
        size="md"
        error={requirementForm.errors?.duration}
        data-testid="requirement-duration"
      />
      <Select
        label="Preferované pohlaví"
        variant="filled"
        value={requirementForm.values.prefSex}
        onChange={e => e && requirementForm.setFieldValue('prefSex', e)}
        data={sexMap ? MappingToComboBoxItem(sexMap) : []}
        comboboxProps={{ shadow: 'md' }}
        radius="md"
        size="md"
        error={requirementForm.errors?.prefSex}
        data-testid="requirement-pref-sex"
      />

      <Textarea
        label="Poznámka"
        autosize
        maxRows={3}
        variant="filled"
        {...requirementForm.getInputProps('note')}
        radius="md"
        size="md"
        data-testid="requirement-note"
      />
      <MultiSelect
        label="Výběr tagů"
        variant="filled"
        data={tags !== null ? tags?.content.map(tag => tag.name) : []}
        value={
          requirementForm.values.tags !== null
            ? requirementForm.values.tags.map(r => r.name)
            : []
        }
        onChange={vals => {
          const arr: Tag[] = [];
          vals.forEach(tag => {
            if (tags !== undefined) {
              const t = tags.content.find(element => element.name === tag);
              t !== undefined && arr.push(t);
            }
          });
          requirementForm.setFieldValue('tags', arr);
        }}
        comboboxProps={{ shadow: 'md' }}
        radius="md"
        size="md"
        error={requirementForm.errors?.tags}
        data-testid="requirement-tags"
      />
      <Group>
        <Button
          variant="outline"
          radius="md"
          size="md"
          onClick={closeModal}
          className="flex-1 text-lg"
          data-testid="cancel-button"
        >
          Zrušit
        </Button>
        <Button
          variant="filled"
          radius="md"
          type="submit"
          size="md"
          className="flex-1 text-lg"
          data-testid="submit-button"
        >
          Potvrdit
        </Button>
      </Group>
    </form>
  );
};
